import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../contexts/SimpleAuthContext';
import API_CONFIG from '../../config/apiConfig';
import { useToast } from '../../contexts/ToastContext';
import InterviewModal from '../../components/registrations/InterviewModal';
import PlacementModal from '../../components/registrations/PlacementModal';
import SlideOver from '../../components/common/SlideOver';
import './RecruitmentDashboard.css';
import { 
    ArrowPathIcon, 
    DocumentTextIcon, 
    CheckCircleIcon, 
    XCircleIcon, 
    CalendarIcon, 
    BriefcaseIcon,
    ChevronRightIcon,
    UserIcon,
    BuildingOfficeIcon,
    AcademicCapIcon,
    MagnifyingGlassIcon,
    EnvelopeIcon,
    PhoneIcon,
    IdentificationIcon,
    ClockIcon,
    ExclamationCircleIcon,
    InboxArrowDownIcon
} from '@heroicons/react/24/outline';

const RecruitmentDashboard = () => {
    const { makeAuthenticatedRequest } = useAuth();
    const { showToast } = useToast();
    const [registrations, setRegistrations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('');
    const [viewDetailsId, setViewDetailsId] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIds, setSelectedIds] = useState([]);
    
    // Derived state for the slide-over
    const selectedRegistration = registrations.find(r => r.id === viewDetailsId);
    const isSlideOverOpen = !!viewDetailsId;

    // Selection helpers
    const toggleSelect = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
        );
    };

    const toggleSelectAll = () => {
        if (selectedIds.length === filteredRegistrations.length) {
            setSelectedIds([]);
        } else {
            setSelectedIds(filteredRegistrations.map(r => r.id));
        }
    };
    
    // Modals
    const [showInterviewModal, setShowInterviewModal] = useState(false);
    const [showPlacementModal, setShowPlacementModal] = useState(false);
    const [selectedRegistrationId, setSelectedRegistrationId] = useState(null);

    const fetchRegistrations = useCallback(async () => {
        setLoading(true);
        try {
            const response = await makeAuthenticatedRequest(
                API_CONFIG.registrations.adminList(statusFilter)
            );
            if (response.ok) {
                const data = await response.json();
                setRegistrations(data);
            } else {
                showToast('Failed to fetch registrations', 'error');
            }
        } catch (error) {
            console.error(error);
            showToast('Error loading data', 'error');
        } finally {
            setLoading(false);
        }
    }, [makeAuthenticatedRequest, statusFilter, showToast]);

    useEffect(() => {
        fetchRegistrations();
    }, [fetchRegistrations]);

    const handleVerifyDocument = async (docId, status, reason = '') => {
        try {
            const response = await makeAuthenticatedRequest(
                API_CONFIG.registrations.verifyDocument(docId),
                {
                    method: 'POST',
                    body: JSON.stringify({ status, rejection_reason: reason })
                }
            );
            if (response.ok) {
                showToast(`Document ${status}`, 'success');
                fetchRegistrations(); 
            } else {
                showToast('Failed to update document status', 'error');
            }
        } catch (error) {
            console.error(error);
            showToast('Error verifying document', 'error');
        }
    };

    const handleViewDetails = (registration) => {
        setViewDetailsId(registration.id);
    };

    const openInterviewModal = (regId) => {
        setSelectedRegistrationId(regId);
        setShowInterviewModal(true);
    };

    const openPlacementModal = (regId) => {
        setSelectedRegistrationId(regId);
        setShowPlacementModal(true);
    };

    // Filter registrations locally by search term
    const filteredRegistrations = useMemo(() => {
        return registrations.filter(reg => {
            if (!searchTerm) return true;
            const term = searchTerm.toLowerCase();
            const fullName = `${reg.user_details?.first_name} ${reg.user_details?.last_name}`.toLowerCase();
            const email = reg.user_details?.email?.toLowerCase() || '';
            const course = reg.course?.title?.toLowerCase() || '';
            const idNumber = reg.user_details?.id_number?.toLowerCase() || '';
            return fullName.includes(term) || email.includes(term) || course.includes(term) || idNumber.includes(term);
        });
    }, [registrations, searchTerm]);

    const getStatusStyles = (status) => {
        const iconClass = "recruitment-dashboard__h-4 recruitment-dashboard__w-4";
        switch (status) {
            case 'applied': 
                return { 
                    className: 'status-applied',
                    icon: <InboxArrowDownIcon className={iconClass} />
                };
            case 'verification_pending': 
                return { 
                    className: 'status-verification_pending',
                    icon: <ClockIcon className={iconClass} />
                };
            case 'verified': 
                return { 
                    className: 'status-verified',
                    icon: <CheckCircleIcon className={iconClass} />
                };
            case 'verification_failed': 
                return { 
                    className: 'status-verification_failed',
                    icon: <XCircleIcon className={iconClass} />
                };
            case 'interview_scheduled': 
                return { 
                    className: 'status-interview_scheduled',
                    icon: <CalendarIcon className={iconClass} />
                };
            case 'placed': 
                return { 
                    className: 'status-placed',
                    icon: <BriefcaseIcon className={iconClass} />
                };
            default: 
                return { 
                    className: 'status-default',
                    icon: <DocumentTextIcon className={iconClass} />
                };
        }
    };

    const stats = useMemo(() => ({
        total: registrations.length,
        pending: registrations.filter(r => r.status === 'applied' || r.status === 'verification_pending').length,
        interview: registrations.filter(r => r.status === 'interview_scheduled').length,
        placed: registrations.filter(r => r.status === 'placed').length
    }), [registrations]);

    return (
        <div className="recruitment-dashboard">
            {/* Enhanced Header Section */}
            <div className="recruitment-dashboard__header">
                <div className="recruitment-dashboard__container">
                    <div className="recruitment-dashboard__header-content">
                        <div>
                            <div className="recruitment-dashboard__title-wrapper">
                                <div className="recruitment-dashboard__icon-wrapper">
                                    <BriefcaseIcon className="recruitment-dashboard__h-6 recruitment-dashboard__w-6 text-amber-500" />
                                </div>
                                <div>
                                    <h1 className="recruitment-dashboard__title">
                                        Recruitment Management
                                    </h1>
                                    <p className="recruitment-dashboard__subtitle">
                                        Monitor applicant progress, verify compliance documents, and facilitate candidate placements across host employers.
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        <div>
                            <button 
                                onClick={fetchRegistrations}
                                className="recruitment-dashboard__refresh-btn"
                            >
                                <ArrowPathIcon className={`recruitment-dashboard__h-4 recruitment-dashboard__w-4 ${loading ? 'animate-spin' : ''}`} />
                                Refresh
                            </button>
                        </div>
                    </div>

                    {/* Enhanced Stats Cards */}
                    <div className="recruitment-dashboard__stats-grid">
                        {[
                            { label: 'Total Applicants', value: stats.total, color: 'gray', icon: UserIcon },
                            { label: 'Requires Action', value: stats.pending, color: 'amber' },
                            { label: 'In Interview', value: stats.interview, color: 'indigo' },
                            { label: 'Placed', value: stats.placed, color: 'emerald' },
                        ].map((stat) => (
                            <div key={stat.label} className={`recruitment-dashboard__stat-card ${stat.color === 'amber' ? 'recruitment-dashboard__stat-card--amber' : ''}`}>
                                <div className={`recruitment-dashboard__stat-icon recruitment-dashboard__stat-icon--${stat.color}`}>
                                    {stat.icon ? <stat.icon className="recruitment-dashboard__h-6 recruitment-dashboard__w-6" /> : (
                                        stat.color === 'amber' ? <ClockIcon className="recruitment-dashboard__h-6 recruitment-dashboard__w-6" /> :
                                        stat.color === 'indigo' ? <CalendarIcon className="recruitment-dashboard__h-6 recruitment-dashboard__w-6" /> :
                                        <CheckCircleIcon className="recruitment-dashboard__h-6 recruitment-dashboard__w-6" />
                                    )}
                                </div>
                                <div className="flex-1">
                                    <div className="recruitment-dashboard__stat-label">{stat.label}</div>
                                    <div className="recruitment-dashboard__stat-value">{stat.value}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <div className="recruitment-dashboard__container recruitment-dashboard__content">
                
                {/* Search & Filters Container */}
                <div className="recruitment-dashboard__filters">
                    <div className="recruitment-dashboard__filter-row">
                        <div className="recruitment-dashboard__search-wrapper">
                            <div className="recruitment-dashboard__search-icon">
                                <MagnifyingGlassIcon className="recruitment-dashboard__h-4 recruitment-dashboard__w-4" />
                            </div>
                            <input
                                type="text"
                                placeholder="Search by name, email, or course..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="recruitment-dashboard__search-input"
                            />
                        </div>

                        <div className="recruitment-dashboard__divider"></div>

                        <div className="recruitment-dashboard__tabs">
                            {[
                                { name: 'All', value: '' },
                                { name: 'New', value: 'applied' },
                                { name: 'Pending Docs', value: 'verification_pending' },
                                { name: 'Verified', value: 'verified' },
                                { name: 'Interviewing', value: 'interview_scheduled' },
                                { name: 'Placed', value: 'placed' },
                            ].map((tab) => (
                                <button
                                    key={tab.value}
                                    onClick={() => setStatusFilter(tab.value)}
                                    className={`recruitment-dashboard__tab-btn ${statusFilter === tab.value ? 'recruitment-dashboard__tab-btn--active' : 'recruitment-dashboard__tab-btn--inactive'}`}
                                >
                                    {tab.name}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Applications Data Table */}
                <div className="recruitment-dashboard__table-container">
                    <div className="recruitment-dashboard__table-wrapper">
                        <table className="recruitment-dashboard__table">
                            <thead>
                                <tr className="recruitment-dashboard__table-header">
                                    <th className="recruitment-dashboard__th">
                                        <input 
                                            type="checkbox" 
                                            checked={filteredRegistrations.length > 0 && selectedIds.length === filteredRegistrations.length}
                                            onChange={toggleSelectAll}
                                        />
                                    </th>
                                    <th className="recruitment-dashboard__th">Applicant Details</th>
                                    <th className="recruitment-dashboard__th">Programme & Campus</th>
                                    <th className="recruitment-dashboard__th">Applied On</th>
                                    <th className="recruitment-dashboard__th">Current Status</th>
                                    <th className="recruitment-dashboard__th text-right">Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredRegistrations.length > 0 ? (
                                    filteredRegistrations.map((reg) => {
                                        const statusStyle = getStatusStyles(reg.status);
                                        return (
                                            <tr 
                                                key={reg.id}
                                                className={`recruitment-dashboard__tr ${selectedIds.includes(reg.id) ? 'recruitment-dashboard__tr--selected' : ''}`}
                                                onClick={() => handleViewDetails(reg)}
                                            >
                                                <td className="recruitment-dashboard__td" onClick={(e) => e.stopPropagation()}>
                                                    <input 
                                                        type="checkbox" 
                                                        checked={selectedIds.includes(reg.id)}
                                                        onChange={() => toggleSelect(reg.id)}
                                                    />
                                                </td>
                                                <td className="recruitment-dashboard__td">
                                                    <div className="recruitment-dashboard__user-cell">
                                                        <div className="recruitment-dashboard__avatar">
                                                            {reg.user_details?.first_name?.[0]}{reg.user_details?.last_name?.[0]}
                                                        </div>
                                                        <div className="recruitment-dashboard__user-info recruitment-dashboard__min-w-0">
                                                            <div className="recruitment-dashboard__user-name recruitment-dashboard__truncate">
                                                                {reg.user_details?.first_name} {reg.user_details?.last_name}
                                                            </div>
                                                            <div className="recruitment-dashboard__user-email recruitment-dashboard__truncate">
                                                                <EnvelopeIcon className="recruitment-dashboard__h-3-5 recruitment-dashboard__w-3-5 recruitment-dashboard__shrink-0" />
                                                                <span className="recruitment-dashboard__truncate">{reg.user_details?.email}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="recruitment-dashboard__td">
                                                    <div className="text-sm font-medium text-gray-900 flex items-center gap-1.5">
                                                        <AcademicCapIcon className="recruitment-dashboard__h-4 recruitment-dashboard__w-4 recruitment-dashboard__shrink-0 text-amber-500" />
                                                        <span className="recruitment-dashboard__truncate">{reg.course?.title}</span>
                                                    </div>
                                                    <div className="text-xs text-gray-500 flex items-center gap-1.5 mt-0.5">
                                                        <BuildingOfficeIcon className="recruitment-dashboard__h-3-5 recruitment-dashboard__w-3-5 recruitment-dashboard__shrink-0" />
                                                        <span className="recruitment-dashboard__truncate">{reg.course?.campus?.name}</span>
                                                    </div>
                                                </td>
                                                <td className="recruitment-dashboard__td whitespace-nowrap">
                                                    <div className="text-sm text-gray-600 font-medium">
                                                        {new Date(reg.application_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                                    </div>
                                                    <div className="text-[10px] text-gray-400 uppercase font-bold tracking-tighter">
                                                        {new Date(reg.application_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </div>
                                                </td>
                                                <td className="recruitment-dashboard__td whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                    <div className="recruitment-dashboard__status-wrapper">
                                                        <span className={`recruitment-dashboard__status-badge ${statusStyle.className}`}>
                                                            <span className="recruitment-dashboard__status-dot dot"></span>
                                                            {reg.status_display}
                                                        </span>
                                                        
                                                        <div className="recruitment-dashboard__status-dropdown">
                                                            {[
                                                                { label: 'Verify', status: 'verified', icon: CheckCircleIcon, color: 'text-emerald-600' },
                                                                { label: 'Reject', status: 'verification_failed', icon: XCircleIcon, color: 'text-red-600' },
                                                                { label: 'Awaiting', status: 'verification_pending', icon: ClockIcon, color: 'text-amber-600' },
                                                            ].map(opt => (
                                                                <button
                                                                    key={opt.status}
                                                                    onClick={async () => {
                                                                        try {
                                                                            const response = await makeAuthenticatedRequest(
                                                                                `${API_CONFIG.API_URL}/registrations/admin/update-status/${reg.id}/`,
                                                                                {
                                                                                    method: 'POST',
                                                                                    body: JSON.stringify({ status: opt.status })
                                                                                }
                                                                            );
                                                                            if (response.ok) {
                                                                                showToast(`Status updated to ${opt.label}`, 'success');
                                                                                fetchRegistrations();
                                                                            } else {
                                                                                showToast('Failed to update status', 'error');
                                                                            }
                                                                        } catch (error) {
                                                                            showToast('Error updating status', 'error');
                                                                        }
                                                                    }}
                                                                    className="recruitment-dashboard__dropdown-item"
                                                                >
                                                                    <opt.icon className={`recruitment-dashboard__h-3-5 recruitment-dashboard__w-3-5 ${opt.color}`} />
                                                                    {opt.label}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="recruitment-dashboard__td text-right">
                                                    <button 
                                                        onClick={(e) => { e.stopPropagation(); handleViewDetails(reg); }}
                                                        className="inline-flex items-center gap-1 text-sm font-semibold text-amber-600 hover:text-amber-700"
                                                    >
                                                        Manage
                                                        <ChevronRightIcon className="recruitment-dashboard__h-4 recruitment-dashboard__w-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-20 text-center">
                                            <div className="flex flex-col items-center max-w-xs mx-auto">
                                                <div className="p-4 bg-gray-50 rounded-2xl mb-4">
                                                    <MagnifyingGlassIcon className="h-10 w-10 text-gray-300" />
                                                </div>
                                                <h3 className="text-base font-bold text-gray-900">No applicants found</h3>
                                                <p className="text-sm text-gray-500 mt-1 text-center">We couldn't find any applications matching your current filter or search criteria.</p>
                                                <button 
                                                    onClick={() => {setStatusFilter(''); setSearchTerm('');}}
                                                    className="mt-4 text-sm font-bold text-amber-600 hover:underline"
                                                >
                                                    Clear all filters
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Redesigned SlideOver for Details */}
            <SlideOver
                isOpen={isSlideOverOpen}
                onClose={() => setViewDetailsId(null)}
                title="Applicant Profile & Workflow"
                width="recruitment-dashboard__slideover-width"
            >
                {selectedRegistration && (
                    <div className="recruitment-dashboard__slideover-content">
                        {/* Profile Overview Card */}
                        <div className="recruitment-dashboard__profile-card">
                            <div className="recruitment-dashboard__profile-bg-icon">
                                <IdentificationIcon className="h-32 w-32" />
                            </div>
                            
                            <div className="recruitment-dashboard__profile-inner">
                                <div className="recruitment-dashboard__profile-avatar">
                                    {selectedRegistration.user_details?.first_name?.[0]}{selectedRegistration.user_details?.last_name?.[0]}
                                </div>
                                <div className="recruitment-dashboard__profile-info">
                                    <div className="recruitment-dashboard__profile-header">
                                        <h2 className="recruitment-dashboard__profile-name">
                                            {selectedRegistration.user_details?.first_name} {selectedRegistration.user_details?.last_name}
                                        </h2>
                                        <span className="recruitment-dashboard__profile-tag">
                                            {selectedRegistration.status_display}
                                        </span>
                                    </div>
                                    <div className="recruitment-dashboard__profile-details">
                                        <div className="recruitment-dashboard__profile-detail-item">
                                            <EnvelopeIcon className="recruitment-dashboard__h-4 recruitment-dashboard__w-4 text-amber-400" />
                                            {selectedRegistration.user_details?.email}
                                        </div>
                                        <div className="recruitment-dashboard__profile-detail-item">
                                            <IdentificationIcon className="recruitment-dashboard__h-4 recruitment-dashboard__w-4 text-amber-400" />
                                            {selectedRegistration.user_details?.id_number || 'No ID Provided'}
                                        </div>
                                        <div className="recruitment-dashboard__profile-detail-item">
                                            <PhoneIcon className="recruitment-dashboard__h-4 recruitment-dashboard__w-4 text-amber-400" />
                                            {selectedRegistration.user_details?.phone_number || 'No Phone'}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="recruitment-dashboard__details-grid">
                            {/* Left Column: Workflow & Actions (3/5) */}
                            <div className="recruitment-dashboard__left-col">
                                
                                {/* Status Progress Tracker */}
                                <div className="recruitment-dashboard__workflow">
                                    <h3 className="recruitment-dashboard__section-title">
                                        <ClockIcon className="recruitment-dashboard__h-4 recruitment-dashboard__w-4 text-amber-500" />
                                        Workflow Progression
                                    </h3>
                                    
                                    <div className="recruitment-dashboard__timeline">
                                        <div className="recruitment-dashboard__timeline-line"></div>
                                        
                                        <div className="recruitment-dashboard__timeline-items">
                                            {[
                                                { label: 'Application Received', status: 'applied', date: selectedRegistration.application_date },
                                                { label: 'Document Verification', status: 'verified', date: selectedRegistration.documents?.find(d => d.status === 'verified')?.updated_at },
                                                { label: 'Interview Phase', status: 'interview_passed', date: selectedRegistration.interviews?.[0]?.scheduled_date },
                                                { label: 'Final Placement', status: 'placed', date: selectedRegistration.placement_details?.created_at },
                                            ].map((step, idx) => {
                                                const isDone = ['placed', 'interview_passed', 'verified', 'applied'].indexOf(selectedRegistration.status) >= ['placed', 'interview_passed', 'verified', 'applied'].indexOf(step.status);
                                                return (
                                                    <div key={step.label} className="recruitment-dashboard__timeline-item">
                                                        <div className={`recruitment-dashboard__timeline-dot ${
                                                            isDone ? 'recruitment-dashboard__timeline-dot--active' : 'recruitment-dashboard__timeline-dot--inactive'
                                                        }`}>
                                                            {isDone ? <CheckCircleIcon className="h-5 w-5" /> : <div className="h-2 w-2 rounded-full bg-current"></div>}
                                                        </div>
                                                        <div>
                                                            <div className={`text-sm font-bold ${isDone ? 'text-gray-900' : 'text-gray-400'}`}>{step.label}</div>
                                                            {step.date && <div className="text-xs text-gray-400 mt-0.5">{new Date(step.date).toLocaleDateString()}</div>}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                {/* Main Actions Grid */}
                                <div className="recruitment-dashboard__actions-grid">
                                    <button 
                                        onClick={() => openPlacementModal(selectedRegistration.id)}
                                        className="recruitment-dashboard__action-card recruitment-dashboard__action-card--purple group"
                                    >
                                        <div className="recruitment-dashboard__action-icon recruitment-dashboard__action-icon--purple">
                                            <BriefcaseIcon className="h-5 w-5" />
                                        </div>
                                        <div className="recruitment-dashboard__action-title">Placement</div>
                                        <div className="recruitment-dashboard__action-desc">Allocate candidate to a host employer.</div>
                                    </button>

                                    <button 
                                        onClick={() => openInterviewModal(selectedRegistration.id)}
                                        className="recruitment-dashboard__action-card recruitment-dashboard__action-card--indigo group"
                                    >
                                        <div className="recruitment-dashboard__action-icon recruitment-dashboard__action-icon--indigo">
                                            <CalendarIcon className="h-5 w-5" />
                                        </div>
                                        <div className="recruitment-dashboard__action-title">Interviews</div>
                                        <div className="recruitment-dashboard__action-desc">Schedule or update interview records.</div>
                                    </button>
                                </div>

                                {/* Current Placement Info (If exists) */}
                                {selectedRegistration.placement_details && (
                                    <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-100">
                                        <h4 className="text-xs font-black uppercase tracking-widest text-emerald-600 mb-4">Current Placement Details</h4>
                                        <div className="flex items-start gap-4">
                                            <div className="p-3 bg-white rounded-xl shadow-sm text-emerald-600 shrink-0">
                                                <BuildingOfficeIcon className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <div className="font-bold text-gray-900 text-lg">{selectedRegistration.placement_details.host_employer}</div>
                                                <div className="text-sm text-emerald-700 font-medium">{selectedRegistration.placement_details.job_title}</div>
                                                <div className="mt-3 flex items-center gap-2">
                                                    <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-md font-bold">
                                                        {selectedRegistration.placement_details.status_display}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Right Column: Documents (2/5) */}
                            <div className="recruitment-dashboard__right-col">
                                <div className="recruitment-dashboard__workflow sticky top-0">
                                    <h3 className="recruitment-dashboard__section-title justify-between">
                                        <div className="flex items-center gap-2">
                                            <DocumentTextIcon className="recruitment-dashboard__h-4 recruitment-dashboard__w-4 text-amber-500" />
                                            Documents
                                        </div>
                                        <span className="text-[10px] font-bold text-gray-400 bg-gray-50 px-2 py-1 rounded">
                                            {selectedRegistration.documents?.length || 0} TOTAL
                                        </span>
                                    </h3>
                                    
                                    <div className="recruitment-dashboard__doc-list">
                                        {selectedRegistration.documents?.length > 0 ? (
                                            selectedRegistration.documents.map(doc => (
                                                <div key={doc.id} className="recruitment-dashboard__doc-item">
                                                    <div className="recruitment-dashboard__doc-header">
                                                        <div className="flex-1 min-w-0">
                                                            <div className="recruitment-dashboard__doc-title">
                                                                {doc.document_type_display}
                                                            </div>
                                                            <a 
                                                                href={doc.file} 
                                                                target="_blank" 
                                                                rel="noopener noreferrer" 
                                                                className="recruitment-dashboard__doc-link"
                                                            >
                                                                View File <ChevronRightIcon className="h-2 w-2" />
                                                            </a>
                                                        </div>
                                                        <div className={`p-1 rounded-lg ${
                                                            doc.status === 'verified' ? 'bg-emerald-50 text-emerald-600' : 
                                                            doc.status === 'rejected' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'
                                                        }`}>
                                                            {doc.status === 'verified' ? <CheckCircleIcon className="h-4 w-4" /> : 
                                                             doc.status === 'rejected' ? <XCircleIcon className="h-4 w-4" /> : <ClockIcon className="h-4 w-4" />}
                                                        </div>
                                                    </div>

                                                    {doc.status === 'pending' && (
                                                        <div className="recruitment-dashboard__doc-actions">
                                                            <button 
                                                                onClick={() => handleVerifyDocument(doc.id, 'verified')}
                                                                className="recruitment-dashboard__btn-approve"
                                                            >
                                                                Approve
                                                            </button>
                                                            <button 
                                                                onClick={() => {
                                                                    const reason = prompt('Reason for rejection:');
                                                                    if (reason) handleVerifyDocument(doc.id, 'rejected', reason);
                                                                }}
                                                                className="recruitment-dashboard__btn-reject"
                                                            >
                                                                Reject
                                                            </button>
                                                        </div>
                                                    )}
                                                    
                                                    {doc.status === 'rejected' && doc.rejection_reason && (
                                                        <div className="mt-2 p-2 bg-red-50 rounded-lg text-[10px] text-red-700 font-medium">
                                                            <span className="font-bold">Reason:</span> {doc.rejection_reason}
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        ) : (
                                            <div className="text-center py-10">
                                                <ExclamationCircleIcon className="h-8 w-8 text-gray-200 mx-auto mb-2" />
                                                <p className="text-xs text-gray-400 font-medium">No documents available</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </SlideOver>

            {/* Sticky Batch Actions Bar */}
            {selectedIds.length > 0 && (
                <div className="recruitment-dashboard__batch-bar">
                    <div className="recruitment-dashboard__batch-content">
                        <div className="recruitment-dashboard__batch-count">
                            <span className="recruitment-dashboard__batch-badge">
                                {selectedIds.length}
                            </span>
                            <span className="text-sm font-medium text-gray-300">Selected</span>
                        </div>
                        
                        <div className="recruitment-dashboard__batch-actions">
                            <button 
                                onClick={() => {
                                    showToast(`Batch update for ${selectedIds.length} items (Placeholder)`, 'info');
                                }}
                                className="recruitment-dashboard__btn-batch"
                            >
                                <CheckCircleIcon className="h-4 w-4 text-emerald-400" />
                                Verify All
                            </button>
                            
                            <button 
                                onClick={() => setSelectedIds([])}
                                className="recruitment-dashboard__btn-batch text-gray-400"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <InterviewModal 
                isOpen={showInterviewModal}
                onClose={() => setShowInterviewModal(false)}
                registrationId={selectedRegistrationId}
                onSuccess={fetchRegistrations}
            />

            <PlacementModal
                isOpen={showPlacementModal}
                onClose={() => setShowPlacementModal(false)}
                registrationId={selectedRegistrationId}
                onSuccess={fetchRegistrations}
            />
        </div>
    );
};

export default RecruitmentDashboard;
